import type {
  DataSetTagSummary,
  DataTagMetadata,
  DataTagSummary,
} from '@robovision/data'
import { RecordWithAutocomplete } from './Autocomplete'


export interface TagParams {
  attributes: Record<string, string>
  createdAt: Date
  description?: string
  name: string
  ownerId?: string
  poolId: string
  sampleCount?: number
  slug: string
  tagId: string
}


/**
 * Not all of these attributes will be present
 * and all of them can have other values
 * but we want to be able to autocomplete those
 * attributes which are known to be present sometimes
 */
export type TagAttributes = RecordWithAutocomplete<{
  algorithmJobId: string
  artifactId: string
  edge_recording_id: string
  // imageViews is a json string representation of string[]
  imageViews: string
  importJobId: string
  jobType:
    | 'IMPORT'
    | 'EXPORT'
    | 'CLASS_IMPORT'
    | 'CLASS_EXPORT'
    | 'DATA_EXCHANGE'
  origin:
    | 'PLATFORM'
    | 'CUSTOM'
  tagType: 'IMPORT' | 'EXPORT' | 'RECORDING' | 'STANDARD' | 'INFERENCE'
}>

export class Tag {
  public readonly attributes: TagAttributes
  public readonly createdAt: Date
  public readonly description?: string
  public readonly name: string
  public readonly ownerId?: string
  public readonly poolId: string
  public sampleCount?: number
  public readonly slug: string
  public readonly tagId: string

  constructor (params: TagParams) {
    this.attributes = params.attributes
    this.createdAt = params.createdAt
    this.description = params.description
    this.name = params.name
    this.ownerId = params.ownerId
    this.poolId = params.poolId
    this.sampleCount = params.sampleCount
    this.slug = params.slug
    this.tagId = params.tagId
  }

  public withUpdates (updates: Partial<TagParams>) {
    return new Tag({
      attributes: this.attributes,
      createdAt: this.createdAt,
      description: this.description,
      name: this.name,
      ownerId: this.ownerId,
      poolId: this.poolId,
      sampleCount: this.sampleCount,
      slug: this.slug,
      tagId: this.tagId,
      ...updates,
    })
  }

  public get mutable () {
    // we should get this as a constant from the BE somehow
    // attributes.jobType is old nomenclature, but still supported for existing
    // imports
    return (
      this.attributes?.jobType !== 'IMPORT' &&
      this.attributes?.tagType !== 'IMPORT' &&
      this.attributes?.tagType !== 'RECORDING' &&
      this.attributes?.tagType !== 'INFERENCE'
    )
  }

  public get isTemporary () {
    return !this.ownerId && !this.poolId
  }

  public static getSampleCount (data: DataTagSummary | DataSetTagSummary | DataTagMetadata) {
    return (
      // Legacy first
      'sampleCount' in data && data.sampleCount !== null
      // Then set
        ? data.sampleCount : 'setSampleCount' in data && data.setSampleCount !== null
        // Then pool
          ? data.setSampleCount : 'poolSampleCount' in data && data.poolSampleCount !== null
            ? data.poolSampleCount : undefined
    ) as number | undefined
  }

  public static fromResponse (data: DataTagSummary | DataSetTagSummary | DataTagMetadata) {
    return new Tag({
      attributes: data.attributes,
      createdAt: new Date(data.createdAt),
      description: data.description,
      name: data.name,
      ownerId: data.ownerId,
      poolId: data.poolId,
      sampleCount: Tag.getSampleCount(data),
      slug: data.slug,
      tagId: data.tagId,
    })
  }
}
